import { Star } from 'lucide-react';
import { cn } from '@/client/lib/utils';

// Read-only star rating display. `value` may be fractional; renders 5 stars
// filled proportionally to the value (rounded to half-stars for clarity).
export function StarRatingDisplay({
  value,
  size = 16,
  className,
}: {
  value: number;
  size?: number;
  className?: string;
}) {
  const rounded = Math.round(value * 2) / 2;
  return (
    <span
      className={cn('inline-flex gap-0.5', className)}
      aria-label={`${value.toFixed(1)} z 5`}
    >
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = rounded >= n;
        const half = !filled && rounded >= n - 0.5;
        return (
          <span
            key={n}
            className="relative inline-block"
            style={{ width: size, height: size }}
          >
            <Star
              size={size}
              className="absolute inset-0 text-glass-border-strong"
              fill="currentColor"
              strokeWidth={0}
            />
            {(filled || half) && (
              <span
                className="absolute inset-0 overflow-hidden"
                style={{ width: half ? size / 2 : size }}
              >
                <Star
                  size={size}
                  className="text-warning drop-shadow-[0_1px_2px_rgba(212,150,10,0.35)]"
                  fill="currentColor"
                  strokeWidth={0}
                />
              </span>
            )}
          </span>
        );
      })}
    </span>
  );
}

// Interactive star input. Hover/click to set, value 1..5.
export function StarRatingInput({
  value,
  onChange,
  size = 28,
  disabled,
}: {
  value: number;
  onChange: (next: number) => void;
  size?: number;
  disabled?: boolean;
}) {
  return (
    <div className="inline-flex gap-1.5" role="radiogroup" aria-label="Hodnotenie">
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = value >= n;
        return (
          <button
            type="button"
            key={n}
            disabled={disabled}
            onClick={() => onChange(n)}
            className={cn(
              'rounded-md p-0.5 transition-all duration-200 ease-out',
              'hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
              'disabled:opacity-50 disabled:pointer-events-none',
              filled
                ? 'text-warning drop-shadow-[0_2px_4px_rgba(212,150,10,0.35)]'
                : 'text-glass-border-strong hover:text-warning/70',
            )}
            aria-checked={value === n}
            role="radio"
            aria-label={`${n} ${n === 1 ? 'hviezda' : n < 5 ? 'hviezdy' : 'hviezd'}`}
          >
            <Star size={size} fill="currentColor" strokeWidth={0} />
          </button>
        );
      })}
    </div>
  );
}
